import { useState, useMemo } from "react";
import { toast } from "sonner";
import { X, DiamondPlus, Gem, Globe, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Toggle } from "@/components/ui/toggle";
import SubscribeInput from "./SubscribeInput";
import {
  COHORT_LABELS,
  VALID_COHORT_KEYS,
  type CohortKey,
} from "@/utils/constants";

type EmailToastProps = {
  toastId: string | number;
};

const COHORT_ICONS: Record<CohortKey, LucideIcon> = {
  new_features: DiamondPlus,
  promotions: Gem,
  international_shipping: Globe,
};

const COHORTS = VALID_COHORT_KEYS.map((key) => ({
  key,
  label: COHORT_LABELS[key],
  icon: COHORT_ICONS[key],
}));

export default function EmailToast({ toastId }: EmailToastProps) {
  const [selected, setSelected] = useState<Set<CohortKey>>(new Set());
  const toggle = (key: CohortKey) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const selectedArray = useMemo(() => Array.from(selected), [selected]);

  const dismiss = () => {
    toast.dismiss(toastId);
  };

  return (
    <div className="flex w-full flex-col gap-6 rounded-lg border border-[#dbdbdb] bg-white px-4 pb-6 pt-4">
      <div className="flex flex-col">
        <div className="mb-4 flex items-center justify-between gap-4">
          <span className="font-trimMono text-[14px] uppercase leading-[1.6] tracking-[0.42px] text-[#1f2022]">
            Get the latest from Matic
          </span>
          <button onClick={dismiss} aria-label="Dismiss">
            <X
              size={24}
              strokeWidth={1.5}
              className="shrink-0 text-[#1f2022]"
            />
          </button>
        </div>
        <div className="mb-6 h-px w-full bg-[#dbdbdb]" />
        <div className="flex flex-col gap-2">
          <span className="pb-1 font-trimMono text-[10px] uppercase tracking-[0.3px] text-[#54524e]">
            I&apos;m interested in...
          </span>
          <div className="flex flex-wrap gap-x-[11px] gap-y-3">
            {COHORTS.map((c) => {
              const active = selected.has(c.key);
              return (
                <Toggle
                  key={c.key}
                  pressed={active}
                  onPressedChange={() => toggle(c.key)}
                  variant="outline"
                  size="sm"
                  className={cn(
                    "h-[30px] w-fit min-w-0 gap-2 rounded-lg border-0 px-3 py-2 font-trimMono text-[10px] font-normal uppercase leading-[1.6] tracking-[0.3px] shadow-none",
                    active
                      ? "bg-[#1f2022] text-white hover:bg-[#1f2022] hover:text-white data-[state=on]:bg-[#1f2022] data-[state=on]:text-white"
                      : "bg-[#f5f3ef] text-[#54524e] hover:bg-[#f5f3ef] hover:text-[#54524e]",
                  )}
                >
                  <c.icon
                    size={16}
                    strokeWidth={1}
                    className={cn(
                      "size-4 shrink-0",
                      active ? "text-white" : "text-[#8D8B84]",
                    )}
                  />
                  {c.label}
                </Toggle>
              );
            })}
          </div>
        </div>
      </div>
      <SubscribeInput
        source="email_toast"
        variant="boxed"
        selectedOptions={selectedArray}
        onSuccess={() => toast.dismiss(toastId)}
      />
    </div>
  );
}
